import React from 'react';
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import {Box, makeStyles, TextField, Tooltip, Typography} from "@material-ui/core";
import {Link} from "react-router-dom";
import moment from "moment";

const CARD_GAP = '1rem';

const useStyles = makeStyles((theme) => ({
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: CARD_GAP,
		alignItems: 'stretch',
		width: '100%',
	},
	row: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: '0.5rem',
		minWidth: 0,
	},
	input: {
		textAlign: 'right',
		width: '7rem',
		flexShrink: 0,
		fontSize: '1.25rem',
		fontWeight: 'bold',
	},
	label: {
		fontSize: '1.25rem',
		fontWeight: 'bold',
		cursor: 'default',
		flex: '1 1 auto',
		minWidth: 0,
		lineHeight: 1.3,
		overflowWrap: 'anywhere',
	},
	linkLabel: {
		fontSize: '1.25rem',
		fontWeight: 'bold',
		lineHeight: 1.3,
		overflowWrap: 'anywhere',
	},
	alertLabel: {
		fontSize: '1.25rem',
		fontWeight: 'bold',
		cursor: 'default',
		color: '#d32f2f',
		flex: '1 1 auto',
		minWidth: 0,
		lineHeight: 1.3,
		overflowWrap: 'anywhere',
	},
	alertInput: {
		textAlign: 'right',
		width: '7rem',
		flexShrink: 0,
		fontSize: '1.25rem',
		fontWeight: 'bold',
		color: '#d32f2f',
	},
	card: {
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	cardContent: {
		flex: 1,
		paddingTop: theme.spacing(1.5),
		paddingBottom: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		'&:last-child': {
			paddingBottom: theme.spacing(2),
		},
	},
	header: {
		textAlign: 'center',
		backgroundColor: '#cacaca',
		borderBottom: '1px solid #ddd',
		padding: theme.spacing(1, 1.5),
	},
	headerTitle: {
		fontSize: '1.35rem !important',
		fontWeight: 'bold',
		lineHeight: 1.25,
		width: '100%',
	},
	financialsHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		width: '100%',
		gap: theme.spacing(1),
	},
	financialsHeaderLabel: {
		fontSize: '1.15rem',
		fontWeight: 'bold',
		lineHeight: 1.25,
		flex: '1 1 0',
		minWidth: 0,
		overflowWrap: 'anywhere',
	},
	financialsHeaderLabelRight: {
		textAlign: 'right',
	},
}));

const renderNameList = (names) => {
	if (!names || names.length === 0) {
		return 'None';
	}
	return (
		<div>
			{names.map((name, index) => <div key={index}>{name}</div>)}
		</div>
	);
};

const renderReleaseList = (people) => {
	if (!people || people.length === 0) {
		return 'None';
	}
	return (
		<div>
			{people.map((person, index) => (
				<div key={index}>
					{person.name}{person.onboarding_date ? ` - ${moment(person.onboarding_date).format('MMM D, YYYY')}` : ''}
				</div>
			))}
		</div>
	);
};

const InfoRow = ({classes, label, value, tooltip, alertLow}) => (
	<Box className={classes.row}>
		<Tooltip title={tooltip || ''} arrow placement="left">
			<Typography className={alertLow ? classes.alertLabel : classes.label}>{label}</Typography>
		</Tooltip>
		<TextField
			defaultValue={value}
			InputProps={{
				disableUnderline: true,
				classes: {input: alertLow ? classes.alertInput : classes.input}
			}}
			fullWidth={false}
			variant="standard"
		/>
	</Box>
);

const getAvInfoRows = (filterDate) => [
	{
		key: 'BitDefender Keys Available',
		label: 'BitDefender Keys Available',
		alertType: 'bitdefender',
		tooltip: (threshold) => `Unused BitDefender license pool. Alert threshold: ${threshold}. Turns red when at or below threshold.`,
	},
	{
		key: 'MBAM Keys Available',
		label: 'MBAM Keys Available',
		alertType: 'malwarebytes',
		tooltip: (threshold) => `Unused Malwarebytes keys in inventory. Alert threshold: ${threshold}. Turns red when at or below threshold.`,
	},
	{
		key: 'Total Active Keys',
		label: 'Total Active Licenses',
		tooltip: () => 'Active Stripe AV subscriptions (active + past due), summed by license quantity across BitDefender Pro, BitDefender Plus, and Malwarebytes Premium.',
	},
	{
		key: 'sold_on_date',
		label: `Sold on ${moment(filterDate).format('MMM D, YYYY')}`,
		tooltip: () => `AV keys sold on ${moment(filterDate).format('MMMM D, YYYY')} (matched by sold date).`,
	},
	{
		key: 'Inactive Subscriptions',
		label: 'AV Renewal Removals',
		linkTo: '/managers/av_center',
		tooltip: () => 'Unresolved inactive AV subscription records requiring attention.',
	},
];

const ExecutiveDashboard = ({av_data, av_alert_thresholds, wo_data, booking_data, tor_data, hiring_data, av_financials, av_metrics_cached_at, filterDate, forceRefresh}) => {
	const classes = useStyles();
	const avInfoRows = getAvInfoRows(filterDate);

	const hiring = hiring_data || {};
	const newAccounts = hiring.new_accounts || hiring.offer_paperwork || {count: 0, names: []};
	const pendingBgCheck = hiring.pending_bg_check || {count: 0, names: []};
	const inTraining = hiring.in_training || {count: 0, names: []};
	const expectedRelease = hiring.expected_release || [];
	const expectedReleaseValue = expectedRelease.length > 0 && expectedRelease[0].onboarding_date
		? moment(expectedRelease[0].onboarding_date).format('MMM D, YYYY')
		: 'N/A';

	const financials = av_financials || {items: [], annual_total: 0};
	const thresholds = av_alert_thresholds || {bitdefender: 0, malwarebytes: 0};
	const cardHeaderTitleProps = {align: 'center', variant: 'h6', className: classes.headerTitle};
	const avMetricsLastSynced = av_metrics_cached_at
		? moment(av_metrics_cached_at).format('MMM D, YYYY h:mm A')
		: 'today';
	const avFinancialsTooltip = (
		<div>
			<div>Subscription counts and projected revenue are synced from Stripe once per day.</div>
			<div style={{marginTop: 8}}>Last synced: {avMetricsLastSynced}.</div>
			<div style={{marginTop: 8}}>For real-time subscription data, log in to the Stripe dashboard.</div>
		</div>
	);

	const isAvKeyLow = (alertType, value) => {
		if (!alertType) {
			return false;
		}
		const count = Number(value) || 0;
		const threshold = Number(thresholds[alertType]) || 0;
		return count <= threshold;
	};

	return (
		<div className={classes.grid} key={forceRefresh}>
			<Card id={'books-card'} className={classes.card}>
					<CardHeader
						title="Booking Info"
						titleTypographyProps={cardHeaderTitleProps}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						<InfoRow classes={classes} label="Net Books" value={booking_data.net_books}
						         tooltip="Tickets created on this day, minus tickets cancelled on this day."/>
						<InfoRow classes={classes} label="New" value={booking_data.new_books}
						         tooltip="Tickets created this day for new customers (not flagged as existing)."/>
						<InfoRow classes={classes} label="Existing" value={booking_data.existing_books}
						         tooltip="Tickets created this day for existing customers."/>
						<InfoRow classes={classes} label="Cancels" value={booking_data.cancels}
						         tooltip="Tickets with a cancellation timestamp on this day."/>
						<InfoRow classes={classes} label="TFOs" value={booking_data.tfos}
						         tooltip="Phone logs of call type 4 (TFO) created on this day."/>
						<InfoRow classes={classes} label="Book %"
						         value={`${booking_data.book_percent ? Math.round(booking_data.book_percent * 100) : "N/A"}%`}
						         tooltip="Net books divided by the total number of phone logs created this day."/>
					</CardContent>
				</Card>

				<Card id={'wo-card'} className={classes.card}>
					<CardHeader
						title="Work Order Info"
						titleTypographyProps={cardHeaderTitleProps}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						<InfoRow classes={classes} label="Gross" value={`$${wo_data.gross}`}
						         tooltip="Total labor amount plus virus sales from completed (financialized) work orders on this day."/>
						<InfoRow classes={classes} label="FEs Working" value={wo_data.fes_working}
						         tooltip="Number of distinct field engineers with a non-cancelled work order scheduled this day."/>
						<InfoRow classes={classes} label="Avg WO" value={`$${wo_data.avg_wo}`}
						         tooltip="Average gross revenue per completed work order (Gross / Completed)."/>
						<InfoRow classes={classes} label="Total WOs" value={wo_data.total_wos}
						         tooltip="All non-cancelled work orders with an appointment on this day."/>
						<InfoRow classes={classes} label="Completed" value={wo_data.completed_wos}
						         tooltip="Work orders that have financials entered (a labor amount greater than 0)."/>
						<InfoRow classes={classes} label="Outstanding" value={wo_data.outstanding_wos}
						         tooltip="Total work orders minus completed ones (not yet financialized)."/>
					</CardContent>
				</Card>

				<Card id={'tors-card'} className={classes.card}>
					<CardHeader
						title="TOR Info"
						titleTypographyProps={cardHeaderTitleProps}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						<InfoRow classes={classes} label="FE TOR Count" value={tor_data.fe_tor_count}
						         tooltip="Number of approved / not-showing-up time-off requests for Field Engineers overlapping this day."/>
						<InfoRow classes={classes} label="FE TOR" value={tor_data.fe_tor_amount}
						         tooltip="Field Engineer time-off for this day measured in person-days (full day = 1, half day = 0.5)."/>
						<InfoRow classes={classes} label="Dispatch TOR Count" value={tor_data.dispatch_tor_count}
						         tooltip="Number of approved / not-showing-up time-off requests for Dispatchers overlapping this day."/>
						<InfoRow classes={classes} label="Dispatch TOR" value={tor_data.dispatch_tor_amount}
						         tooltip="Dispatcher time-off for this day measured in person-days (full day = 1, half day = 0.5)."/>
						<InfoRow classes={classes} label="Next Day FE TORs" value={tor_data.next_day_fe_tors}
						         tooltip="Count of Field Engineer time-off requests overlapping the next day."/>
						<InfoRow classes={classes} label="Next Day Dispatch TORs" value={tor_data.next_day_dispatch_tors}
						         tooltip="Count of Dispatcher time-off requests overlapping the next day."/>
					</CardContent>
				</Card>

				<Card id={'hiring-card'} className={classes.card}>
					<CardHeader
						title="Hiring Info"
						titleTypographyProps={cardHeaderTitleProps}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						<Box className={classes.row}>
							<Tooltip title={`Candidates in Scheduled Interview status with an interview date of ${moment(filterDate).format('MMM D, YYYY')}. Click to view the scheduled interviews.`} arrow placement="left">
								<span><Link to="/hiring/scheduled_interview" className={classes.linkLabel}>Interviews on {moment(filterDate).format('MMM D, YYYY')}</Link></span>
							</Tooltip>
							<TextField
								defaultValue={hiring.interviews_today ?? 0}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>

						<Box className={classes.row}>
							<Tooltip title={renderNameList(pendingBgCheck.names)} arrow placement="left">
								<Typography className={classes.label}>Pending BG Check</Typography>
							</Tooltip>
							<TextField
								defaultValue={pendingBgCheck.count ?? 0}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>

						<Box className={classes.row}>
							<Tooltip title={renderNameList(newAccounts.names)} arrow placement="left">
								<Typography className={classes.label}>New Accounts</Typography>
							</Tooltip>
							<TextField
								defaultValue={newAccounts.count ?? 0}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>

						<Box className={classes.row}>
							<Tooltip title={renderNameList(inTraining.names)} arrow placement="left">
								<Typography className={classes.label}>In Training</Typography>
							</Tooltip>
							<TextField
								defaultValue={inTraining.count ?? 0}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>

						<Box className={classes.row}>
							<Tooltip title={renderReleaseList(expectedRelease)} arrow placement="left">
								<Typography className={classes.label}>Expected Release Date</Typography>
							</Tooltip>
							<TextField
								defaultValue={expectedReleaseValue}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>
					</CardContent>
				</Card>

			<Card id={'av-info-card'} className={classes.card}>
					<CardHeader
						title="Anti-Virus Info"
						titleTypographyProps={cardHeaderTitleProps}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						{av_data && avInfoRows.map((row) => {
							const value = av_data[row.key] ?? 0;
							const alertLow = isAvKeyLow(row.alertType, value);
							const tooltipText = typeof row.tooltip === 'function'
								? row.tooltip(thresholds[row.alertType] ?? 0)
								: row.tooltip;

							if (row.linkTo) {
								return (
									<Box className={classes.row} key={row.key}>
										<Tooltip title={tooltipText} arrow placement="left">
											<span><Link to={row.linkTo} className={classes.linkLabel}>{row.label}</Link></span>
										</Tooltip>
										<TextField
											defaultValue={value}
											InputProps={{disableUnderline: true, classes: {input: classes.input}}}
											fullWidth={false}
											variant="standard"
										/>
									</Box>
								);
							}

							return (
								<InfoRow
									key={row.key}
									classes={classes}
									label={row.label}
									value={value}
									tooltip={tooltipText}
									alertLow={alertLow}
								/>
							);
						})}
					</CardContent>
				</Card>

				<Tooltip title={avFinancialsTooltip} arrow placement="top">
				<span style={{display: 'block'}}>
				<Card id={'av-financials-card'} className={classes.card}>
					<CardHeader
						title={
							<Box className={classes.financialsHeader}>
								<Typography component="span" className={classes.financialsHeaderLabel}>
									Anti-Virus Financials
								</Typography>
								<Typography component="span" className={`${classes.financialsHeaderLabel} ${classes.financialsHeaderLabelRight}`}>
									Annual Projected
								</Typography>
							</Box>
						}
						titleTypographyProps={{component: 'div', align: 'center', className: classes.headerTitle}}
						className={classes.header}
					/>
					<CardContent className={classes.cardContent}>
						{financials.items && financials.items.map((item) => (
							<Box className={classes.row} key={item.label}>
								<Tooltip title={`${item.active} active subscription licenses at $${item.price}/yr each`} arrow placement="left">
									<Typography className={classes.label}>{item.label} x {item.active}</Typography>
								</Tooltip>
								<TextField
									defaultValue={`$${Number(item.annual).toLocaleString()}`}
									InputProps={{disableUnderline: true, classes: {input: classes.input}}}
									fullWidth={false}
									variant="standard"
								/>
							</Box>
						))}
						<Box className={classes.row}>
							<Typography className={classes.label}>Total</Typography>
							<TextField
								defaultValue={`$${Number(financials.annual_total || 0).toLocaleString()}`}
								InputProps={{disableUnderline: true, classes: {input: classes.input}}}
								fullWidth={false}
								variant="standard"
							/>
						</Box>
					</CardContent>
				</Card>
				</span>
				</Tooltip>
		</div>
	)
}

export default ExecutiveDashboard;
